/** 로그인 전 화면과 사업자 정보 블록이 읽는 값.
 *
 * **이 파일이 그 값의 유일한 자리다.** 로그인 전 화면(`P-01`~`P-04`)과 모든 웹 화면 맨
 * 아래의 사업자 정보 블록이 여기서 읽는다. 화면마다 상호나 번호를 문자열로 적으면 한 자리를
 * 고쳤을 때 나머지가 옛 값을 계속 내보낸다. 약관 · 처리방침 본문이 대표자 · 연락처 · 호스팅
 * 제공자를 말할 때도 글자로 다시 적지 않고 아래 상수를 읽는다(`scripts/public-pages.test.mjs`
 * 「사업자 정보 값은 public-copy.ts 한 곳에서만 온다」가 웹 소스에 그 글자가 박히면 실패한다).
 *
 * **값은 전부 사용자가 2026-10-04에 준 실제 값이다.** 사업자 정보 여덟 칸은 사용자 값이고,
 * 시행일 · 돌아가는 때 · 제공기간은 PRD `7.0` 「로그인 전 화면의 확정 문구」(2026-10-04
 * `design-manager` 확정. B안 사용자 결정 위)를 옮겼다. 지금 자리 표시는 하나도 없다.
 *
 * ⛔ **자리 표시를 단 채로 게시하지 않는다**(법무 `L-F54` · PRD `7.0` 「로그인 전 화면」
 * 표의 「공통 요소 · 사업자 정보」 행). 자리 표시인지의 판정은 `isPlaceholderValue` 한 곳이다.
 * 막는 자리(게이트)는 `apps/web/scripts/assert-public-copy.mjs` 다. 이 모듈이 내보내는 문자열
 * 상수와 `{ value }` 배열 항목 전부를 이 함수로 판정하고, 운영 웹 빌드(`apps/web/package.json`
 * 의 운영 빌드 스크립트) 앞에서 돌아 하나라도 남으면 빌드를 멈춘다. dev 빌드는 경고만 하고 통과한다.
 * ⚠️ 게이트는 화면 본문 문자열(약관 · 처리방침 조문)의 자리 표시는 못 잡는다(법무 `6A.15`(4)ⓑ).
 *
 * ⛔ **이 파일은 다른 모듈을 import 하지 않는다.** 게이트가 이 파일 하나를 Node 타입 스트리핑으로
 * 바로 import 하는데, 저장소의 확장자 없는 상대 경로(`./retention`)는 Node 에서 풀리지 않는다.
 * 그래서 아래 `30` 처럼 다른 모듈의 상수를 쓸 자리도 글자로 두고 근거를 단다.
 *
 * 칸 이름 `이메일` 은 법령의 낱말(`전자우편주소`)이 아니라 셀러 화면(`A-48`)의 낱말에
 * 맞춘 것이다(PRD `7.5.9` 용어 표). 같은 것을 두 낱말로 부르지 않기 위해서다.
 *
 * ⚠️ **여덟 칸(상호 + 일곱 칸)은 법정 표시 항목과 조문 대조를 마친 목록이 아니다**
 * (`M-31`(d)). 목업의 칸을 그대로 옮겼을 뿐이다. 항목을 더하거나 빼는 판단은 법무 검토를
 * 거친다.
 *
 * ⛔ **D1 에 아무 칸을 두지 않는다. 마이그레이션 0건이다.** 셀러 데이터가 아니라 우리 회사의
 * 고정 문구라 요청마다 읽을 이유가 없고, 배포와 함께 바뀌는 것이 맞다
 * (`docs/DATA_DICTIONARY.md` 39장).
 */

/** 사업자 정보 블록의 한 칸. `label` 은 화면에 그대로 나가는 칸 이름이다. */
export interface BusinessInfoField {
  readonly label: string;
  readonly value: string;
}

/** 상호. 블록 맨 위 굵은 줄. */
export const BUSINESS_NAME = "온다(onda)";

/** 대표자. 사업자 정보 블록과 처리방침 제10조(개인정보 보호책임자. 2026-10-04 사용자 결정 ④)가 읽는다. */
export const REPRESENTATIVE_NAME = "박지선";

/** 유선 전화번호. 사업자 정보 블록과 처리방침 제10조가 읽는다. */
export const CONTACT_PHONE = "0507-2097-2440";

/** 연락처 이메일. **한 메일함이다.** 사업자 정보 블록의 `이메일` 칸, 환불정책 제5조, 처리방침
 *  제10조, 웹 콘솔 돈 문장(`money-copy.ts` `UNMADE_SHEET_RETURN_NOTICE`)이 모두 이 상수를 읽는다.
 *  2026-10-04 사용자 결정 ⑤: 이 메일함은 사용자 본인이 보고 토스 관리자 화면에서 직접 취소한다.
 *  (옛 `REFUND_CONTACT` 는 같은 값을 따로 들고 있어서 지웠다. 2026-10-04) */
export const CONTACT_EMAIL = "support@withonda.co";

/** 호스팅 제공자. 사업자 정보 블록과 처리방침 제5조 · 제6조(처리를 맡기는 곳 · 국외 이전)가 읽는다. */
export const HOSTING_PROVIDER = "Cloudflare, Inc.";

/** 상호 아래 일곱 칸. 순서는 목업 그대로다. 값은 위 상수와 한 자리다. */
export const BUSINESS_INFO_FIELDS: readonly BusinessInfoField[] = [
  { label: "대표자", value: REPRESENTATIVE_NAME },
  { label: "사업자등록번호", value: "786-48-01143" },
  { label: "통신판매업신고번호", value: "제 2026-인천서해-0898호" },
  { label: "사업장 주소", value: "인천광역시 서해구 가정로 437, 102-4101" },
  { label: "유선 전화번호", value: CONTACT_PHONE },
  { label: "이메일", value: CONTACT_EMAIL },
  { label: "호스팅 제공자", value: HOSTING_PROVIDER },
];

/** 우리가 파는 것의 이름(PRD 7.5.9). P-01 상품 이름 · P-02 머리 줄이 쓴다. */
export const SERVICE_PRODUCT_NAME = "상세페이지 이미지 만들기";

/* ── 시행일. 문서마다 따로 둔다(법무 `L-F57` 선행조건 3 · `6A.15`(2)) ──────────────────
 *
 * 옛 `POLICY_EFFECTIVE_DATE` 하나를 세 문서가 함께 읽었다. 그러면 한 문서를 고쳐 시행일을 바꿀 때
 * 다른 두 문서의 게시 판까지 조용히 바뀐다(`L-X2`). 그래서 갈랐다. **한 문서를 고칠 때는 그 문서의
 * 상수만 바꾼다.**
 *
 * - 지금 값은 셋 다 `2026.10.04`(사용자 값). **운영 공개일**(결제사 심사 제출과 운영 공개 중 이른
 *   때)이 늦어지면 그날로 바꿀 수 있다. 공개일보다 앞선 날짜가 심사 문서에 남지 않게 하기 위해서다.
 * - ⛔ **앞으로의 개정판은 시행일이 게시일보다 앞설 수 없다.** 셀러가 읽기 전에 바뀐 조건으로
 *   묶이는 꼴이 된다.
 * - **2026-10-10 개정**(결과 받기를 결제한 날부터 1개월로. 토스 카드사 심사 보완 요청): 약관 · 처리방침만
 *   `2026.10.10` 으로 바꿨고 환불정책은 바뀌지 않아 그대로다. 셀러에게 불리한 변경(약관 제3조 2 의 30일
 *   고지 대상, 처리방침 제11조도 같은 7일 · 30일 약속이다. 실제로 불리하게 바뀐 문서는 처리방침이다)이지만
 *   게시일 시행으로 했다. 근거는 법무 예비 검토(`6A.19`): 운영 D1 실측(2026-10-10)에서 완료된 결제 0건,
 *   결과(판) 0건이라 옛 조건으로 결과를 받은 셀러가 없고, 알려야 할 상대도 없다. 두 문서의 고지 약속을
 *   어기는 것으로 읽히는 문제는 이 사실 위에서만 성립하므로, 실측이 바뀌면 시행일을 옮긴다. 선행조건은 배포 직전 완료 결제 0건 재실측이다
 *   (`docs/LEGAL_IP_GUIDE.md` 기록). 머지가 늦어지면 실제 게시일로 옮긴다. */

/** P-02 환불정책 머리 줄의 시행일. */
export const REFUND_POLICY_EFFECTIVE_DATE = "2026.10.04";
/** P-03 이용약관 머리 줄의 시행일. */
export const TERMS_EFFECTIVE_DATE = "2026.10.10";
/** P-04 개인정보처리방침 머리 줄의 시행일. */
export const PRIVACY_POLICY_EFFECTIVE_DATE = "2026.10.10";

/** 취소한 뒤 금액이 셀러에게 닿기까지의 시간. **결제사 · 카드사 쪽 시간이지 우리 처리 기한이 아니다.**
 *  P-02 제4조 끝 문장이고(`REFUND_RETURN_TIMING` 이 이어 붙인다), 웹 `Account.tsx` 정산 명세 아래
 *  l1 의 앞 문장도 이 상수를 읽는다(PRD `7.0` 「로그인 전 화면의 확정 문구」 (1) 제4조 · (5) 3).
 *  두 자리가 한 상수를 읽으므로 각자 고치지 않는다.
 *  결제 수단을 단정하지 않는다(판단 3). 카드만 열기로 정하면 이 문장 하나를 줄이면 두 자리가 함께 바뀐다. */
export const REFUND_ARRIVAL_NOTICE =
  "취소한 뒤 금액이 돌아가기까지 걸리는 기간은 결제 수단마다 다르며, 카드는 카드사 사정에 따라 3~7영업일이 걸립니다.";

/** P-02 제4조 금액이 돌아가는 때. **B안**(2026-10-04 사용자 결정 ②): 셀러가 알려 주면 확인한 뒤
 *  취소하고, 알려 주지 않은 장은 원본 사진 보관이 끝난 뒤 우리가 확인해 취소한다. **수동 취소다.**
 *  금액을 돌려보내는 코드(`O-70`)는 아직 0건이고, 이 문장을 지키는 것은 **사람**이다. 절차는
 *  `docs/REFUND_RUNBOOK.md`(창구 메일함을 보고 토스 관리자 화면에서 부분 취소).
 *  ⛔ 실행 시점·자동성 표현을 넣지 않는다(PRD `7.0` (1) 「이 화면에 쓰지 않는 말」 · 법무 `6A.12` ·
 *  `L-F50`). 「제5조의 연락처」는 `CONTACT_EMAIL` 이다.
 *  둘째 문장은 **사용자 결정 D2**(2026-10-04): 웹 · 앱에서 그 장을 「만들지 않기」로 고르면 이 조의
 *  「알려 준 것」으로 본다(법무 `L-F56` 선행조건 7). `export-decline.ts` `exportConfirmBody()` 의
 *  「따로 알려 주실 것이 없습니다」가 이 문장 위에서만 참이다. 이 문장을 걷으면 그쪽도 함께 고친다. */
export const REFUND_RETURN_TIMING =
  "제3조의 장 가운데 만들지 않으실 장을 제5조의 연락처로 알려 주시면, 확인한 뒤 결제하신 수단으로 그 장 금액을 취소합니다. " +
  "앱이나 웹에서 그 장을 「만들지 않기」로 고르셔도 알려 주신 것으로 봅니다. " +
  "알려 주시지 않은 장은 원본 사진 보관이 끝난 뒤(제3조 3) 확인해 같은 방법으로 취소합니다. " +
  REFUND_ARRIVAL_NOTICE;

/** P-01 `제공기간` 칸의 값(PRD `7.0` 「로그인 전 화면의 확정 문구」 (2), 2026-10-04 확정).
 *  결제사에 회신하는 「서비스 제공기간」(결제한 날부터 1개월)과 같은 값이어야 한다(법무 `L-F53`).
 *  ⭐ **2026-10-10 사용자 결정**(토스 카드사 심사 보완 요청: 받기 기한까지 포함한 제공기간을 밝혀라).
 *  결과 이미지 받기(내보내기 · 다시 받기)는 **결제한 날부터 1개월**까지이고, 그 뒤 결과물을 지운다.
 *  옛 확정(결과물은 「계정이 있는 동안」 두고 탈퇴할 때 지운다. PRD `O-9`)은 이 결정으로 뒤집혔다.
 *  ⚠️ **1개월을 지키는 서버 장치(받기 차단 · 파기 크론)는 아직 없다.** 다음 서버 PR 이 세우고, 첫 운영
 *  결제가 1개월에 닿기 전에 머지한다. 그 PR 의 실제 기한은 max(결제한 날 + 달력 1개월, 마지막으로
 *  만들 수 있는 날)이다. 2월처럼 1개월이 30일보다 짧아도 다시 만든 결과를 못 받게 되지 않는다.
 *  ⛔ 「유료 회원 12개월」은 미래 계획이다. 그런 상품이 없으니 여기 적지 않는다.
 *  ⛔ 「다운로드」 · 「한 달」 · 「결제일부터」를 쓰지 않는다(PRD `7.5.9`). 기준일 낱말은 「결제한 날」이다.
 *  ⛔ **원본 30일과 결과 1개월은 다른 기한이다.** 결과까지 「30일」이라 적으면 두 시계가 섞인다.
 *  ⚠️ `30` 은 `retention.ts` 의 `SOURCE_RETENTION_DAYS` 와 같은 값이다. 그 상수를 import 하지 않은
 *  이유는 파일 머리말(게이트가 이 파일만 Node 로 바로 import 한다). 그 값을 바꾸면 여기도 바꾼다. */
export const SERVICE_PERIOD =
  "결제한 뒤 시작을 누르면 만들기 시작. 만들기와 다시 만들기는 원본 사진을 올린 날부터 30일까지(원본 사진도 그날까지 보관). 결과 이미지 받기는 결제한 날부터 1개월까지(다시 만들 수 있는 마지막 날이 더 늦으면 그날까지)";

/* ── 공개 문서 개정 고지 (PRD `7.5.17` `A-60` · `W-90` · `P-04b`. 법무 `4.19`(4) · `L-C38`-5) ──────
 *
 * **장치는 범용, 이 값이 문서별 글자다**(법무 `4.19`(4) 8: 문서 · 판 · 시행일 · 바뀐 곳 요약을 값으로).
 * 앱 `A-60` · 웹 `W-90` · 공개 처리방침 `P-04b` 세 자리가 이 값 하나를 읽는다.
 *
 * - `null` 이면 세 자리 모두 숨는다.
 * - **시행일이 자리 표시(`[시행일]`)면 숨는다.** 셀러에게 `[시행일]` 을 보이지 않는다. 시행일을 정하는
 *   것은 메인 세션이다(배포 때).
 * - ⚠️ 운영 웹 빌드 게이트(`assert-public-copy.mjs`)는 최상위 문자열과 `{ value }` 배열만 본다. 이 값은
 *   객체라 게이트 밖이다. 자리 표시를 단 채로 운영에 나가도 빌드는 막히지 않고, 대신 고지가 숨는다.
 *   **그래서 고지 없이 운영 쓰기 스위치(`ACCOUNT_SETTINGS_WRITE`)를 켜는 사고는 이 파일이 막지 않는다**
 *   (법무 `L-C38`-6 은 사람이 지킨다. `docs/DATA_DICTIONARY.md` 10.3b).
 * - 시행일이 지나면 고지가 사라지고(`pendingAmendment` 가 `null`), 공개 처리방침이 바뀐 뒤 판을 그린다
 *   (`amendmentInForce`). 그 뒤 본문에 접어 넣고 이 값을 `null` 로 되돌린다.
 * - ⛔ 「동의」를 받지 않는다. 버튼은 확인이다(법무 `4.19`(2) · (4) 7).
 * - ⛔ D1 에 칸을 두지 않는다. 39장과 같은 이유(우리 회사의 고정 문구, 배포와 함께 바뀐다).
 */

/** 개정 고지가 다루는 공개 문서. 화면 머리 줄의 문서 이름 그대로다. */
export type PolicyDocName = "이용약관" | "개인정보처리방침" | "환불정책";

/** 바뀌는 곳 한 줄. */
export interface PolicyAmendmentChange {
  /** 굵은 조 이름. 예: `제1조 받는 정보` */
  readonly article: string;
  /** `지금: …` 뒤 글자 */
  readonly before: string;
  /** `바뀐 뒤: …` 뒤 글자 */
  readonly after: string;
}

/** 개정 하나. */
export interface PolicyAmendment {
  /** 판 문자열. 기기에 「본 적 있음」을 적는 열쇠라 한 개정에 하나, 바꾸지 않는다. */
  readonly id: string;
  readonly docName: PolicyDocName;
  /** `yyyy.mm.dd`. 그날 0시(한국 시각)부터 바뀐 뒤 판이 시행된다. */
  readonly effectiveDate: string;
  /** 지금 판 전문 경로(웹). */
  readonly currentPath: string;
  /** 바뀐 뒤 판 전문 경로(웹). */
  readonly nextPath: string;
  readonly changes: readonly PolicyAmendmentChange[];
  /** 바뀌는 곳 끝줄. 없으면 안 그린다. */
  readonly footnote: string | null;
  /** `P-04b` 블록 둘째 줄의 바뀌는 곳 한 줄. */
  readonly summary: string;
}

/** 지금 고지할 개정. 기본 배경을 서버에 두기 시작하는 처리방침 개정(PRD 2026-10-27 결정 1 ·
 *  2026-10-28 (다)안). ⛔ 학습 이용 스위치는 이 개정에 싣지 않는다(법무 `L-C38`-1). 싣는 개정은 따로
 *  30일 고지로 간다. */
export const PENDING_POLICY_AMENDMENT: PolicyAmendment | null = {
  id: "privacy-account-settings-1",
  docName: "개인정보처리방침",
  effectiveDate: "[시행일]",
  currentPath: "/privacy",
  nextPath: "/privacy/next",
  changes: [
    {
      article: "제1조 받는 정보",
      before: "없음",
      // 새 작업이 계정 기본 배경으로 시작하므로(`apps/api/src/lib/jobs.ts` readDefaultBackgroundForNewJob)
      // 쓰는 곳에 「첫 배경」을 더한다. PRD 7.5.17 · 법무 4.19(8)ⓒ 2026-10-31.
      after: "기본 배경으로 고르신 색. 앱과 웹에서 같은 색을 쓰게 하고, 새로 만드는 상세페이지의 첫 배경으로 쓰려고 받습니다.",
    },
    {
      article: "제3조 보관 기간",
      before: "없음",
      after: "기본 배경으로 고르신 색은 탈퇴하실 때까지 두고, 탈퇴하시면 지웁니다.",
    },
  ],
  footnote: "이 정보도 제5조 처리 위탁 · 제6조 국외 이전에 들어갑니다.",
  summary: "제1조 받는 정보와 제3조 보관 기간에 기본 배경 줄이 더해집니다.",
};

/** `yyyy.mm.dd` → 그날 0시 KST 의 epoch ms. 형식이 아니거나(자리 표시 포함) 없는 날짜
 *  (`2026.02.30` 같은)면 `null`. 없는 날짜를 받아 주면 `Date.UTC` 가 다음 달로 넘겨 시행일이 조용히 밀린다. */
export function policyDateStartMs(date: string): number | null {
  const m = /^(\d{4})\.(\d{2})\.(\d{2})$/.exec(date);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  const utc = Date.UTC(y, mo, d);
  const check = new Date(utc);
  if (check.getUTCFullYear() !== y || check.getUTCMonth() !== mo || check.getUTCDate() !== d) return null;
  return utc - 9 * 60 * 60 * 1000;
}

/** 고지를 띄울 개정. 없거나 · 시행일이 자리 표시거나 · 이미 시행됐으면 `null`. */
export function pendingAmendment(
  amendment: PolicyAmendment | null = PENDING_POLICY_AMENDMENT,
  now: Date = new Date(),
): PolicyAmendment | null {
  if (!amendment) return null;
  const start = policyDateStartMs(amendment.effectiveDate);
  return start !== null && now.getTime() < start ? amendment : null;
}

/** 이미 시행된 개정. 시행일이 지났으면 그 개정, 아니면 `null`. */
export function amendmentInForce(
  amendment: PolicyAmendment | null = PENDING_POLICY_AMENDMENT,
  now: Date = new Date(),
): PolicyAmendment | null {
  if (!amendment) return null;
  const start = policyDateStartMs(amendment.effectiveDate);
  return start !== null && now.getTime() >= start ? amendment : null;
}

/** 다시 여는 줄의 `월-일`. `2026.11.10` → `11-10`. */
export function amendmentMonthDay(amendment: PolicyAmendment): string {
  return amendment.effectiveDate.slice(5).replace(".", "-");
}

/**
 * 값이 아직 자리 표시인가. 대괄호로 감싼 값을 자리 표시로 본다.
 * 운영 웹 빌드 게이트(`apps/web/scripts/assert-public-copy.mjs`)가 이 함수로 판정한다.
 */
export function isPlaceholderValue(value: string): boolean {
  return /^\[[^\]]+\]$/.test(value);
}
